/**
 * Date/time presentation helpers.
 *
 * The server pre-formats every timestamp it expects the UI to *explain* (`publishedAtDisplay`,
 * `capturedAtDisplay`, bucket `label`s — §5.3, §8.5). What is left are the bare epoch-ms fields of
 * the homescreen and the table metadata columns (`lastScrapedAt`, `nextScrapeAt`, `archivedAt`,
 * `firstSeenAt`, …), which arrive as raw numbers with no display string. Turning those into text is
 * presentation, not calculation — nothing here touches a value envelope.
 *
 * **The format is fixed, not the viewer's locale**: these strings sit next to server-formatted ones
 * on the same screen, so `"11 May 2028 (Mon), 13:55"` has to come out identical on both sides. The
 * parts are therefore requested from a fixed `en-US` formatter and assembled by hand — whole-string
 * `en-GB` would spell September "Sept" in current CLDR and diverge from Luxon's `en-US` "Sep".
 *
 * All calendar rendering honours the app timezone (PRD "Timezone"); an unusable/unknown zone falls
 * back to the browser's own so a bad setting can never blank the screen.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const zoneCache = new Map<string, string | undefined>();

/** `undefined` (= browser default) for an unset or unsupported zone. */
function usableZone(tz: string | null | undefined): string | undefined {
  if (tz === null || tz === undefined || tz.length === 0) return undefined;
  const cached = zoneCache.get(tz);
  if (cached !== undefined || zoneCache.has(tz)) return cached;
  let resolved: string | undefined;
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz }).format(0);
    resolved = tz;
  } catch {
    resolved = undefined;
  }
  zoneCache.set(tz, resolved);
  return resolved;
}

/** Fixed locale for every string this module composes — never the viewer's. */
const LOCALE = 'en-US';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(kind: string, tz: string | null | undefined, options: Intl.DateTimeFormatOptions) {
  const zone = usableZone(tz);
  const cacheKey = `${kind}|${zone ?? '~'}`;
  const cached = formatterCache.get(cacheKey);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat(LOCALE, { ...options, timeZone: zone });
  formatterCache.set(cacheKey, created);
  return created;
}

const NOT_AVAILABLE = '—';

type DateParts = {
  weekday: string;
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
};

/**
 * One parts formatter per zone. `hourCycle: 'h23'` rather than `hour12: false` — the latter yields
 * the h24 clock in `en-US`, where midnight prints as "24:00".
 */
function dateParts(ts: number | null | undefined, tz: string | null | undefined): DateParts | null {
  if (ts === null || ts === undefined || !Number.isFinite(ts)) return null;
  const parts = formatter('parts', tz, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(ts);

  const bag: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of parts) bag[part.type] = part.value;
  const { weekday, day, month, year, hour, minute } = bag;
  if (
    weekday === undefined ||
    day === undefined ||
    month === undefined ||
    year === undefined ||
    hour === undefined ||
    minute === undefined
  ) {
    return null;
  }
  return { weekday, day, month, year, hour, minute };
}

/** "11 May 2028 (Mon), 13:55" — the hover/absolute form, identical to the server's spelling. */
export function formatDateTime(ts: number | null | undefined, tz?: string | null): string {
  const parts = dateParts(ts, tz);
  if (parts === null) return NOT_AVAILABLE;
  return `${parts.day} ${parts.month} ${parts.year} (${parts.weekday}), ${parts.hour}:${parts.minute}`;
}

/** "11 May 2028 (Mon)" — date only. */
export function formatDate(ts: number | null | undefined, tz?: string | null): string {
  const parts = dateParts(ts, tz);
  if (parts === null) return NOT_AVAILABLE;
  return `${parts.day} ${parts.month} ${parts.year} (${parts.weekday})`;
}

/**
 * "11 May 2028" — the weekday-less form for strings that are already a range or a control label
 * (the header range button, the custom-range panel), where a weekday per end is noise.
 */
export function formatDateCompact(ts: number | null | undefined, tz?: string | null): string {
  const parts = dateParts(ts, tz);
  if (parts === null) return NOT_AVAILABLE;
  return `${parts.day} ${parts.month} ${parts.year}`;
}

let relativeFormatter: Intl.RelativeTimeFormat | null = null;

function relative(): Intl.RelativeTimeFormat {
  relativeFormatter ??= new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  return relativeFormatter;
}

/** "3 hours ago" / "in 2 days" — the Last-scraped and Next-scrape cells (PRD homescreen). */
export function formatRelativeTime(ts: number | null | undefined, now = Date.now()): string {
  if (ts === null || ts === undefined || !Number.isFinite(ts)) return '—';
  const deltaMs = ts - now;
  const abs = Math.abs(deltaMs);
  const fmt = relative();
  if (abs < MINUTE_MS) return fmt.format(Math.trunc(deltaMs / 1000), 'second');
  if (abs < HOUR_MS) return fmt.format(Math.trunc(deltaMs / MINUTE_MS), 'minute');
  if (abs < DAY_MS) return fmt.format(Math.trunc(deltaMs / HOUR_MS), 'hour');
  if (abs < 30 * DAY_MS) return fmt.format(Math.trunc(deltaMs / DAY_MS), 'day');
  if (abs < 365 * DAY_MS) return fmt.format(Math.trunc(deltaMs / (30 * DAY_MS)), 'month');
  return fmt.format(Math.trunc(deltaMs / (365 * DAY_MS)), 'year');
}

/** "1m 12s" — scrape-run duration (`durationMs`, §5.1 #10). */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/* ------------------------------------------------------- custom date range */

/** `<input type="date">` value for an epoch ms instant, in the viewer's own calendar. */
export function toDateInputValue(ts: number | null | undefined): string {
  if (ts === null || ts === undefined || !Number.isFinite(ts)) return '';
  const date = new Date(ts);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * `YYYY-MM-DD` → epoch ms at local midnight (or the last millisecond of the day for `end`).
 * The custom range is entered in the viewer's own calendar; the server still bounds by the app
 * timezone when it buckets.
 */
export function fromDateInputValue(value: string, edge: 'start' | 'end'): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date =
    edge === 'start'
      ? new Date(year, month - 1, day, 0, 0, 0, 0)
      : new Date(year, month - 1, day, 23, 59, 59, 999);
  const ts = date.getTime();
  return Number.isFinite(ts) ? ts : null;
}

/** Weekday index (0 = Sunday) of a `YYYY-MM-DD` calendar date — heatmap layout only. */
export function calendarWeekday(isoDate: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return 0;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
}

/** "Aug" for the heatmap month ruler. */
export function calendarMonthLabel(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return '';
  const ts = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return formatter('month-utc', 'UTC', { month: 'short' }).format(ts);
}
